import { useInterpret, useSelector } from '@xstate/react'
import routes from 'core/routes'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { useStore } from 'stores'
import { AnyInterpreter } from 'xstate'
import {
  ERpEvent,
  ERpState,
  RussianPokerMachineRuntime,
  createRussianPokerMachine,
} from '../machines/RussianPoker'
import { RussianPokerStore } from '../stores/RussianPoker'
import { createMockGameController } from '../utils/devMockGame'
import { logRpMachineState } from '../utils/xstateLogger'
import { useRpSocketContext } from './RpSocketProvider'

export type RpService = AnyInterpreter

interface RussianPokerContextValue {
  store: RussianPokerStore
  rpService: RpService
  isConnected: boolean
  isJoined: boolean
  joinGame: () => Promise<boolean>
  leaveGame: () => void
  forceLeaveGame: () => void
}

const RussianPokerContext = createContext<RussianPokerContextValue | null>(null)

interface RussianPokerProviderProps {
  gameId: string
  children: ReactNode
}

export const RussianPokerProvider = ({
  gameId,
  children,
}: RussianPokerProviderProps) => {
  const { emit, emitWithAck, on, off, isConnected } = useRpSocketContext()
  const navigate = useNavigate()
  const {
    loginStore: { accessToken },
  } = useStore()

  const runtimeRef = useRef<RussianPokerMachineRuntime>(null!)

  const [store] = useState(
    () =>
      new RussianPokerStore({
        gameId,
        getSocket: () => runtimeRef.current.socket,
      })
  )

  const [rpMachine] = useState(() =>
    createRussianPokerMachine({ gameId, getRuntime: () => runtimeRef.current })
  )

  const rpService = useInterpret(rpMachine, {
    devTools: process.env.NODE_ENV === 'development',
  })

  runtimeRef.current = {
    store,
    socket: { emit, emitWithAck, on, off },
    accessToken,
    navigateLobby: () => navigate(routes.lobby),
    // Read machine state directly — avoids MobX/XState phase mismatch in event handlers.
    // `dealing` is a compound state (dealingIdle/dealingAnimating), so match by path.
    isInDealingPhase: () => rpService.getSnapshot().matches('joined.playing.dealing'),
  }

  useEffect(() => {
    const sub = rpService.subscribe(logRpMachineState)
    return () => sub.unsubscribe()
  }, [rpService])

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // @ts-expect-error dev debug
      window.rpService = rpService
      // @ts-expect-error dev debug
      window.rpStore = store
      // @ts-expect-error dev debug
      window.mockGame = createMockGameController(rpService, store)
    }
  }, [rpService, store])

  const isJoined = useSelector(rpService, (state) =>
    state.matches(ERpState.Joined)
  )

  const joinGame = useCallback(async () => {
    if (!isConnected) return false

    const snapshot = rpService.getSnapshot()

    if (snapshot.matches(ERpState.Joined)) return true

    return new Promise<boolean>((resolve) => {
      const subscription = rpService.subscribe((nextState) => {
        if (nextState.matches(ERpState.Joined)) {
          subscription.unsubscribe()
          resolve(true)
          return
        }

        if (
          nextState.matches(ERpState.Error) ||
          nextState.matches(ERpState.Left) ||
          nextState.matches(ERpState.Disconnected)
        ) {
          subscription.unsubscribe()
          resolve(false)
        }
      })

      rpService.send({ type: ERpEvent.Join })
    })
  }, [isConnected, rpService])

  const leaveGame = useCallback(() => {
    rpService.send({ type: ERpEvent.Leave })
  }, [rpService])

  const forceLeaveGame = useCallback(() => {
    rpService.send({ type: ERpEvent.ForceLeave })
  }, [rpService])

  useEffect(() => {
    rpService.send({
      type: isConnected
        ? ERpEvent.SocketConnected
        : ERpEvent.SocketDisconnected,
    })
    if (isConnected) {
      rpService.send({ type: ERpEvent.Join })
    }
  }, [isConnected, rpService])

  useEffect(() => {
    return () => {
      const snapshot = rpService.getSnapshot()
      if (
        snapshot.matches(ERpState.Joined) ||
        snapshot.matches(ERpState.Joining)
      ) {
        rpService.send({ type: ERpEvent.Leave })
      }
      store.disposeStores()
    }
  }, [rpService, store])

  const contextValue = useMemo(
    () => ({
      store,
      rpService,
      isConnected,
      isJoined,
      joinGame,
      leaveGame,
      forceLeaveGame,
    }),
    [store, rpService, isConnected, isJoined, joinGame, leaveGame, forceLeaveGame]
  )

  return (
    <RussianPokerContext.Provider value={contextValue}>
      {children}
    </RussianPokerContext.Provider>
  )
}

export const useRpService = (): RpService => {
  const context = useContext(RussianPokerContext)
  if (!context) {
    throw new Error('useRpService must be used within RussianPokerProvider')
  }
  return context.rpService
}

export const useRussianPokerStore = (): RussianPokerStore => {
  const context = useContext(RussianPokerContext)
  if (!context) {
    throw new Error(
      'useRussianPokerStore must be used within RussianPokerProvider'
    )
  }
  return context.store
}

export const useRussianPokerContext = (): RussianPokerContextValue => {
  const context = useContext(RussianPokerContext)
  if (!context) {
    throw new Error(
      'useRussianPokerContext must be used within RussianPokerProvider'
    )
  }
  return context
}
